import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { axios } from "@root/libs/axios";
import type { FormValues } from "./types";

type UseWorksCountProps = {
  countUrl: string;
  formValues: FormValues;
  distanceMeter?: string;
};

export const formValuesToSearchParams = (params: FormValues): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v) searchParams.append(key, String(v));
      });
    } else if (value !== false && value != null && value !== "") {
      // false/null/空文字の条件を除外する
      // サーバー側でエニーキャリア求人の表示判定（AnycareerSearchPattern）が
      // 検索条件のキー構成に依存しているため、未選択の条件はキーごと送信しない
      searchParams.append(key, String(value));
    }
  });
  // サーバー側のガード句(params[:q].blank?)に対応するため、
  // q[...]パラメータが1つもない場合はq[sort]を送信してハッシュとして認識させる
  if (!searchParams.toString().includes("q%5B")) {
    searchParams.append("q[sort]", "updated");
  }

  return searchParams;
};

export const useWorksCount = ({ countUrl, formValues, distanceMeter }: UseWorksCountProps) => {
  const [currentWorksCount, setCurrentWorksCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  const params: FormValues = useMemo(
    () => ({
      ...formValues,
      "q[distance_meter]": distanceMeter ?? "",
    }),
    [formValues, distanceMeter],
  );

  const animateCount = useCallback((startValue: number, endValue: number) => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const diff = endValue - startValue;
    const duration = 800;
    const startTime = performance.now();

    setIsAnimating(true);

    const animate = (currentTime: number) => {
      if (!isMountedRef.current) {
        animationFrameRef.current = null;
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      const value = startValue + diff * eased;
      const nextDisplay = diff > 0 ? Math.floor(value) : Math.ceil(value);

      setCurrentWorksCount(Math.max(0, nextDisplay));

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setCurrentWorksCount(Math.max(0, endValue));
        setIsAnimating(false);
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  const fetchWorksCount = useCallback(async () => {
    const searchParams = formValuesToSearchParams(params);

    const response = await axios.post<{ count: number }>(countUrl, searchParams, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    if (response.data?.count !== undefined && isMountedRef.current) {
      const newCount = response.data.count;
      animateCount(currentWorksCount, newCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countUrl, params, animateCount]);

  useEffect(() => {
    fetchWorksCount();
  }, [fetchWorksCount]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    currentWorksCount,
    isAnimating,
  };
};
